import { Skeleton, useToast } from '@chakra-ui/react';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  createContext,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidV4 } from 'uuid';
import { z } from 'zod';

import { Receivable } from '@finley-technologies/finley-types';

import { useGetLatestAssignmentPipelineRunStatusForImplementationQuery } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';

export type Metadata =
  | Receivable.Assignment.Pipeline.Metadata
  | null
  | undefined; // undefined state implies initial api call has not resolved

export type LatestAssignmentPipelineRunMetadataForImplementationContextType = {
  metadata: Metadata;
  // not a set state dispatch because we have logic surrounding the set state
  setMetadata: (newLoanProduct: Metadata) => void;
};

export const LatestAssignmentPipelineRunMetadataForImplementationContext = createContext<LatestAssignmentPipelineRunMetadataForImplementationContextType | null>(
  null,
);

export const useLatestAssignmentPipelineRunMetadataForImplementationContext = () => {
  const context = useContext(
    LatestAssignmentPipelineRunMetadataForImplementationContext,
  );
  if (context === null) {
    throw new Error(
      'LatestAssignmentPipelineRunMetadataForImplementationContext must be present',
    );
  }

  return context;
};

// Types
const RouteParamsStruct = z.object({
  implementationId: z.string(),
});

type LatestAssignmentPipelineRunMetadataForImplementationProviderProps = {
  renderLoading?: React.ReactElement;
  children: React.ReactNode;
};
export const LatestAssignmentPipelineRunMetadataForImplementationProvider = ({
  renderLoading = <Skeleton minHeight="50vh" />,
  children,
}: LatestAssignmentPipelineRunMetadataForImplementationProviderProps) => {
  const toast = useToast();
  const navigate = useNavigate();

  const params = useParams();
  const { implementationId } = RouteParamsStruct.parse(params);

  const [metadata, setMetadata] = useState<Metadata>();

  const {
    data,
    startPolling,
    stopPolling,
  } = useGetLatestAssignmentPipelineRunStatusForImplementationQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'no-cache',
    variables: {
      implementationId,
    },
  });

  useEffect(() => {
    startPolling(5000);
    return () => {
      stopPolling();
    };
  }, [startPolling, stopPolling]);

  useEffect(() => {
    if (
      data?.latestAssignmentPipelineRunStatusForImplementation !== undefined
    ) {
      setMetadata(data.latestAssignmentPipelineRunStatusForImplementation);
    }
  }, [data]);

  useEffect(() => {
    if (!implementationId) {
      setMetadata(null);
    }
  }, [implementationId, setMetadata]);

  const handleSetStatus = useCallback(
    (newStatus: Metadata) => {
      try {
        if (!implementationId) {
          setMetadata(null);
          navigate('/assignments');
        } else {
          setMetadata(newStatus);
        }
      } catch (err: any) {
        if (err.message) {
          errorToastConfig.title = err.message;
        }
        errorToastConfig.title = 'Error setting pipeline run status';
        errorToastConfig.id = `error-pipeline-run-status-${uuidV4()}`;
        toast(errorToastConfig);
      }
    },
    [implementationId, toast, navigate],
  );

  const context = useMemo(() => {
    return {
      metadata,
      setMetadata: handleSetStatus,
    };
  }, [metadata, handleSetStatus]);

  if (!context) {
    return renderLoading;
  }
  return (
    <LatestAssignmentPipelineRunMetadataForImplementationContext.Provider
      value={context}
    >
      {children}
    </LatestAssignmentPipelineRunMetadataForImplementationContext.Provider>
  );
};
